"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { EnergyEquivalents } from "@/components/tools/physics-mechanics/energy-tables";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  type NumberFormatter,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  energyAtSpeedMultiple,
  isRelativistic,
  KINETIC,
  type KineticKey,
  ROTATIONAL,
  type RotationalKey,
  SPEED_MULTIPLES,
} from "@/lib/tool-kit/physics-mechanics/kinetic";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";

/** English strings. A translated content file overrides these through `ui`. */
export const KINETIC_ENERGY_CALCULATOR_STRINGS = {
  modeLabel: "What is moving?",
  modeTranslation: "An object moving along (KE = ½mv²)",
  modeRotation: "A spinning object (KE = ½Iω²)",
  translationHint:
    "Kinetic energy is the energy of motion. Enter the object's speed, not its velocity vector — the direction does not matter because the speed is squared.",
  rotationHint:
    "A spinning object's energy depends on its moment of inertia I, which says how its mass is spread out about the axis, and on its angular speed ω.",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  energy: "Kinetic energy KE",
  mass: "Mass m",
  speed: "Speed v",
  inertia: "Moment of inertia I",
  angularSpeed: "Angular speed ω",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign, or scientific notation such as 1.5e3.",
  errors: {
    "energy-negative": "Kinetic energy cannot be negative.",
    "mass-not-positive": "Mass must be greater than zero.",
    "speed-zero":
      "At zero speed an object has no kinetic energy, so its mass cannot be found from its energy. Enter a speed above zero.",
    "inertia-not-positive": "The moment of inertia must be greater than zero.",
    "angular-speed-zero":
      "At zero angular speed there is no kinetic energy, so I cannot be found from it. Enter an angular speed above zero.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  equivalentHeading: "The same energy in other units",
  columnUnit: "Unit",
  columnValue: "Energy",
  scalingHeading: "What if the speed changed?",
  columnSpeed: "Speed",
  columnEnergy: "Kinetic energy",
  columnFactor: "Compared with now",
  speedMultiple: "{n} × the speed",
  factorTimes: "{n} ×",
  relativistic:
    "At this speed — more than about 11% of the speed of light — ½mv² is more than 1% below the relativistic value, so treat the result as an underestimate.",
  assumptions:
    "Uses the classical (non-relativistic) formulas, which are accurate for everyday speeds. Kinetic energy is a scalar, so it is never negative and has no direction.",
} as const;

type Mode = "move" | "spin";
type Fmt = NumberFormatter;

const ENERGY_UNITS = [
  "J",
  "kJ",
  "kcal",
  "Wh",
  "kWh",
  "ftlbf",
  "Btu",
  "eV",
] as const;

const INITIAL_MOVE = initialRelationState<KineticKey>(KINETIC, "E", {
  m: { value: "1000", unit: "kg" },
  v: { value: "72", unit: "kph" },
});
const INITIAL_SPIN = initialRelationState<RotationalKey>(ROTATIONAL, "E", {
  I: { value: "0.5", unit: "kgm2" },
  w: { value: "3000", unit: "rpm" },
});

function SpeedScaling({
  energy,
  strings,
  fmt,
  sig,
}: {
  energy: number;
  strings: typeof KINETIC_ENERGY_CALCULATOR_STRINGS;
  fmt: Fmt;
  sig: number;
}) {
  return (
    <div className="space-y-2">
      <p className={LABEL}>{strings.scalingHeading}</p>
      <DataTable
        head={[strings.columnSpeed, strings.columnEnergy, strings.columnFactor]}
        rows={SPEED_MULTIPLES.map((multiple) => [
          t(strings.speedMultiple, { n: fmt(multiple) }),
          withUnit(
            formatSig(fmt, energyAtSpeedMultiple(energy, multiple), sig),
            "J"
          ),
          t(strings.factorTimes, { n: fmt(multiple * multiple) }),
        ])}
      />
    </div>
  );
}

export default function KineticEnergyCalculatorWidget() {
  const strings = useToolUi(KINETIC_ENERGY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("move");
  const [move, setMove] = useState(INITIAL_MOVE);
  const [spin, setSpin] = useState(INITIAL_SPIN);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const moveLabels: Record<KineticKey, string> = {
    E: strings.energy,
    m: strings.mass,
    v: strings.speed,
  };
  const spinLabels: Record<RotationalKey, string> = {
    E: strings.energy,
    I: strings.inertia,
    w: strings.angularSpeed,
  };
  const moveEval = evaluateRelation(KINETIC, move);
  const spinEval = evaluateRelation(ROTATIONAL, spin);
  const energyTable = (joules: number) => (
    <EnergyEquivalents
      columnUnit={strings.columnUnit}
      columnValue={strings.columnValue}
      fmt={fmt}
      heading={strings.equivalentHeading}
      joules={joules}
      sig={sig}
      unitIds={ENERGY_UNITS}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="ke-mode"
        onChange={setMode}
        options={[
          { value: "move", label: strings.modeTranslation },
          { value: "spin", label: strings.modeRotation },
        ]}
        value={mode}
      />
      <p className={HINT}>
        {mode === "move" ? strings.translationHint : strings.rotationHint}
      </p>
      {mode === "move" ? (
        <RelationInputs
          {...common}
          def={KINETIC}
          evaluation={moveEval}
          idPrefix="ke-move"
          labels={moveLabels}
          onState={setMove}
          state={move}
        />
      ) : (
        <RelationInputs
          {...common}
          def={ROTATIONAL}
          evaluation={spinEval}
          idPrefix="ke-spin"
          labels={spinLabels}
          onState={setSpin}
          state={spin}
        />
      )}
      <div className="w-60">
        <SelectField
          id="ke-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "move" ? (
          <RelationResults
            {...common}
            def={KINETIC}
            evaluation={moveEval}
            extras={(all) => (
              <>
                {isRelativistic(all.v) ? (
                  <Notice>{strings.relativistic}</Notice>
                ) : null}
                {energyTable(all.E)}
                <SpeedScaling
                  energy={all.E}
                  fmt={fmt}
                  sig={sig}
                  strings={strings}
                />
                <p className={HINT}>{strings.assumptions}</p>
              </>
            )}
            labels={moveLabels}
            state={move}
          />
        ) : (
          <RelationResults
            {...common}
            def={ROTATIONAL}
            evaluation={spinEval}
            extras={(all) => (
              <>
                {energyTable(all.E)}
                <p className={HINT}>{strings.assumptions}</p>
              </>
            )}
            labels={spinLabels}
            state={spin}
          />
        )}
      </section>
    </>
  );
}
